<template>
  <div
    class="notif-item"
    :class="{ 'notif-item--unread': !notification.read_at, 'notif-item--dense': dense }"
    role="button"
    tabindex="0"
    @click="$emit('open', notification)"
    @keydown.enter.self="$emit('open', notification)"
  >
    <div class="notif-item-main">
      <span class="notif-item-title">{{ titre }}</span>
      <span
        v-if="corps"
        class="notif-item-desc"
        :title="corps"
      >{{ corps }}</span>
      <span class="notif-item-meta">
        <span
          v-if="notification.type_label"
          class="notif-item-tag"
        >{{ notification.type_label }}</span>
        <span :title="dateComplete">{{ dateRelative }}</span>
      </span>
    </div>

    <el-tooltip
      :content="notification.read_at ? 'Marquer comme non lue' : 'Marquer comme lue'"
      placement="left"
    >
      <el-button
        class="notif-item-action"
        text
        circle
        size="small"
        :aria-label="notification.read_at ? 'Marquer comme non lue' : 'Marquer comme lue'"
        @click.stop="$emit('toggle-read', notification)"
      >
        <Icon
          :icon="notification.read_at ? 'mdi:email-outline' : 'mdi:email-open-outline'"
          :width="16"
        />
      </el-button>
    </el-tooltip>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import dayjs from 'dayjs';
import { Icon } from '@iconify/vue';

const props = defineProps({
    notification: { type: Object, required: true },
    // Variante resserrée du popover : deux lignes de contexte au lieu de trois.
    dense: { type: Boolean, default: false },
});

defineEmits(['open', 'toggle-read']);

const titre = computed(() => {
    const d = props.notification.data || {};
    return d.subject || d.title || props.notification.type_label || props.notification.type_key || 'Notification';
});

// `lines` est la forme conventionnelle du payload côté back (elle alimente aussi
// le rendu du canal mail). Sans ce repli, un émetteur qui ne pose que `lines`
// n'affiche que son titre.
const corps = computed(() => {
    const d = props.notification.data || {};
    if (d.reason) return d.reason;
    if (d.body) return d.body;
    return Array.isArray(d.lines) ? d.lines.join(' · ') : '';
});

// Le temps écoulé se lit plus vite qu'une date quand la notification est
// fraîche, et c'est le cas de tout ce qui compte dans la cloche. La date exacte
// reste accessible en infobulle.
const dateRelative = computed(() => {
    const d = dayjs(props.notification.created_at);
    if (!d.isValid()) return '';
    return d.isAfter(dayjs().subtract(7, 'day')) ? d.fromNow() : d.format('DD/MM/YYYY');
});

const dateComplete = computed(() => {
    const d = dayjs(props.notification.created_at);
    return d.isValid() ? d.format('dddd D MMMM YYYY [à] HH:mm') : '';
});
</script>

<style lang="scss" scoped>
.notif-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px 10px 13px;
    border-left: 3px solid transparent;
    border-bottom: 1px solid var(--el-border-color-lighter);
    cursor: pointer;
    transition: background-color 0.15s;

    &:hover,
    &:focus-visible {
        background-color: var(--el-fill-color-light);

        .notif-item-action {
            opacity: 1;
        }
    }

    &:focus-visible {
        outline: 2px solid var(--el-color-primary);
        outline-offset: -2px;
    }

    /* Repère géométrique plutôt que fond coloré : le texte garde son contraste
       et la pile de non-lues se scanne d'un coup d'œil. */
    &--unread {
        border-left-color: var(--el-color-primary);

        .notif-item-title {
            font-weight: 600;
            color: var(--el-text-color-primary);
        }
    }
}

.notif-item-main {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0; /* sans quoi le contenu déborde au lieu de s'ellipser */
    flex: 1;
}

.notif-item-title {
    font-size: 14px;
    font-weight: 500;
    line-height: 1.35;
    color: var(--el-text-color-regular);
    overflow-wrap: anywhere;
}

.notif-item-desc {
    font-size: 13px;
    line-height: 1.4;
    color: var(--el-text-color-regular);
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    overflow: hidden;
    overflow-wrap: anywhere;
}

.notif-item-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--el-text-color-secondary);
}

.notif-item-tag {
    padding: 1px 6px;
    border-radius: 4px;
    background-color: var(--el-fill-color);
    color: var(--el-text-color-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 55%;
}

.notif-item-action {
    opacity: 0;
    flex-shrink: 0;
    transition: opacity 0.15s;

    &:focus-visible {
        opacity: 1;
    }
}

.notif-item--dense {
    padding: 9px 10px 9px 11px;

    .notif-item-title {
        font-size: 13px;
    }

    .notif-item-desc {
        font-size: 12px;
        -webkit-line-clamp: 2;
        line-clamp: 2;
    }
}
</style>
